import { Box } from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, type ReactElement } from 'react'

import type { AppState } from 'state/index'

import { fetchTrxStats } from 'api/trx'

import * as c from 'common/c'
import colors from 'common/colors'

declare module 'components/admin/trx-uploads/index.js' {
  export type TrxRowData = {
    serialNumber: string
    ytd: number
    past7Days: number
    past30Days: number
    missing: number
    awaitingProcessing: number
    invalid: number
  }

  export type trxSummaryColumns = {
    field: string
    headerName: string
    minWidth: number
    maxWidth: number
    flex: number
    renderCell?: (params: { value: number }) => ReactElement
  }
}

export default function TrxUploadsComponent(): ReactElement<any, any> {
  const { t } = useTranslation()
  const permissions = useSelector(
    (state) => (state as AppState).account.permissions,
  )

  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )

  const [rows, setRows] = useState<TrxRowData[]>([])
  const [loading, setLoading] = useState(false)
  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]
  const [columnVisibility, setColumnVisibility] = useState({
    serialNumber: true,
    ytd: true,
    past7Days: true,
    past30Days: true,
    missing: true,
    awaitingProcessing: isAdmin ?? false,
    invalid: isAdmin ?? false,
  })

  const columns: GridColDef[] = [
    {
      field: 'serialNumber',
      headerName: t('serial_number'),
      maxWidth: 300,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ytd',
      headerName: t('year_to_date'),
      minWidth: 70,
      maxWidth: 110,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: 'text.primary',
              fontWeight: 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
    {
      field: 'past7Days',
      headerName: t('past_7_days'),
      minWidth: 180,
      maxWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: 'text.primary',
              fontWeight: 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
    {
      field: 'past30Days',
      headerName: t('past_30_days'),
      minWidth: 180,
      maxWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: 'text.primary',
              fontWeight: 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
    {
      field: 'missing',
      headerName: t('missing'),
      minWidth: 180,
      maxWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: params.value > 0 ? 'error.main' : 'text.primary',
              fontWeight: params.value > 0 ? 'bold' : 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
    {
      field: 'awaitingProcessing',
      headerName: t('awaiting_processing'),
      minWidth: 180,
      maxWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: 'text.primary',
              fontWeight: 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
    {
      field: 'invalid',
      headerName: t('invalid'),
      minWidth: 180,
      maxWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              color: params.value > 0 ? 'error.main' : 'text.primary',
              fontWeight: params.value > 0 ? 'bold' : 'normal',
            }}
          >
            {params.value * 4} {t('kb')}
          </Box>
        )
      },
    },
  ]

  function CustomNoRowsOverlayFields(): ReactElement {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_uploads')}
      </div>
    )
  }
  function fetchData(ignore) {
    fetchTrxStats()
      .then((res) => {
        if (!ignore) {
          const results = res.data
          setRows(results)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    fetchData(ignore)
    return () => {
      ignore = true
    }
  }, [])

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 4,
          width: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1>{t('harvest_data_uploads')} </h1>
        </Box>
        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            height: '100%',
          }}
        >
          <div
            style={{
              flexGrow: 1,
              height: '100%',
              width: '100%',
              marginTop: '2rem',
            }}
          >
            <DataGrid
              autoHeight
              columnVisibilityModel={columnVisibility}
              getRowId={(row) => row.serialNumber}
              onColumnVisibilityModelChange={(model: {
                serialNumber: true
                ytd: false
                past7Days: true
                past30Days: true
                missing: true
                awaitingProcessing: false
                invalid: false
              }) => setColumnVisibility(model)}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 20 } },
                sorting: {
                  sortModel: [{ field: 'serialNumber', sort: 'desc' }],
                },
              }}
              slots={{
                noRowsOverlay: CustomNoRowsOverlayFields,
              }}
              rows={rows}
              columns={columns}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: colors.greyBackground,
                  textTransform: 'uppercase',
                },
              }}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        </Box>
      </Box>
    </>
  )
}
